import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Copy, Pencil, Trash2, Layers } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import WorkoutBuilder from '@/pages/trainer/Workouts/WorkoutBuilder';
import styles from '@/pages/trainer/Workouts/Workouts.module.css';

export default function TrainerTemplates() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [duplicating, setDuplicating] = useState(null); // template a duplicar
  const [targetStudent, setTargetStudent] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get('/workouts', { templates: 'true' })
      .then((res) => setTemplates((res.data || res).workouts || res.data || []))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    api
      .get('/students')
      .then((res) => setStudents((res.data || res).students || res.data || []))
      .catch(() => setStudents([]));
  }, []);

  const remove = async (w) => {
    if (!confirm(t('templates.confirmDeleteTemplate', { name: w.name }))) return;
    try {
      await api.delete(`/workouts/${w.id}`);
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const openDuplicate = (w) => {
    setDuplicating(w);
    setTargetStudent(students[0] ? String(students[0].id) : '');
  };

  const confirmDuplicate = async () => {
    if (!targetStudent) return;
    setBusy(true);
    try {
      // O serviço espera `asTemplate` (false por padrão = vira treino do aluno).
      await api.post(`/workouts/${duplicating.id}/duplicate`, { studentId: Number(targetStudent) });
      setDuplicating(null);
      alert(t('templates.appliedToStudent'));
    } catch (e) {
      alert(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader kicker="Movez · Personal" title={t('nav.templates')}>
        <Button icon={Plus} onClick={() => setCreating(true)}>{t('screens.newTemplate')}</Button>
      </PageHeader>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}><Spinner /></div>
      ) : templates.length === 0 ? (
        <EmptyState icon={Layers} title={t('templates.emptyTitle')} description={t('templates.emptyDescription')}
          action={<Button icon={Plus} onClick={() => setCreating(true)}>{t('screens.newTemplate')}</Button>} />
      ) : (
        <div className={styles.grid}>
          {templates.map((w) => (
            <Card key={w.id} className={styles.card}>
              <div className={styles.head}>
                <div>
                  {w.templateCategory && <Badge variant="accent">{w.templateCategory}</Badge>}
                  <div className={styles.name}>{w.name}</div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className={styles.icon} onClick={() => setEditing(w)} title={t('common.edit')}><Pencil size={15} /></button>
                  <button className={styles.icon} onClick={() => remove(w)} title={t('common.delete')}><Trash2 size={15} /></button>
                </div>
              </div>
              {w.focus && <div className={styles.focus}>{w.focus}</div>}
              <div className={styles.count}>{t('templates.exerciseCount', { count: w.exercises?.length ?? w.exerciseCount ?? 0 })}</div>
              <Button size="sm" variant="outline" icon={Copy} onClick={() => openDuplicate(w)} style={{ marginTop: 8 }}>
                {t('screens.applyToStudent')}
              </Button>
            </Card>
          ))}
        </div>
      )}

      <Modal
        open={creating || !!editing}
        onClose={() => { setCreating(false); setEditing(null); }}
        kicker={editing ? t('common.edit') : t('templates.newKicker')}
        title={editing?.name || t('templates.templateModalTitle')}
        size="xl"
      >
        {(creating || editing) && (
          <WorkoutBuilder
            template
            workout={editing}
            onSaved={() => { setCreating(false); setEditing(null); load(); }}
            onCancel={() => { setCreating(false); setEditing(null); }}
          />
        )}
      </Modal>

      <Modal
        open={!!duplicating}
        onClose={() => setDuplicating(null)}
        kicker={t('templates.applyTemplateKicker')}
        title={duplicating?.name || ''}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDuplicating(null)}>{t('common.cancel')}</Button>
            <Button loading={busy} disabled={!targetStudent} onClick={confirmDuplicate}>{t('templates.apply')}</Button>
          </>
        }
      >
        <Select
          label={t('templates.student')}
          value={targetStudent}
          onChange={(e) => setTargetStudent(e.target.value)}
          placeholder={students.length ? t('templates.selectPlaceholder') : t('templates.noStudents')}
          options={students.map((s) => ({ value: String(s.id), label: s.name }))}
        />
      </Modal>
    </>
  );
}
